import { IconAngleRight, IconSearch } from '@maia/icons';
import {
  ButtonLink,
  Input,
  PageHeader,
  Pagination,
  RadioGroup,
  ToggleButtonGroup,
  UserRadioCard,
} from '@maia/ui';
import { formatAddress } from '@maia/utils';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { debounce } from 'es-toolkit';
import { useMemo, useState } from 'react';
import { getCustomersOptions } from '../../../features/customer/customers/queryOptions';
import { CustomerInformation } from '../../../features/shared/customer/customer-information';
import { useFilter } from '../../../utils/filter/filter';
import { transformSearchToStateObject } from '../../../utils/filter/utils';
import { validateSearch } from '../../../utils/pagination';

export const Route = createFileRoute('/_authenticated/add-insurance/')({
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient }, deps: search }) => {
    const searchOptioons = transformSearchToStateObject(search);
    return await queryClient.ensureQueryData(
      getCustomersOptions(searchOptioons),
    );
  },
  validateSearch: validateSearch,
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const [activeGuid, setActiveGuid] = useState<undefined | string>(undefined);
  const {
    state,
    dispatch: setSearch,
    mappedState,
  } = useFilter('/add-insurance');

  const {
    data: { response: customers, pagination },
  } = useQuery({ ...getCustomersOptions(mappedState), initialData: data });

  const activeCustomer = useMemo(
    () => customers.find(({ guid }) => guid === activeGuid),
    [customers, activeGuid],
  );

  const debouncedQuery = debounce(
    (payload: string) => setSearch({ type: 'setQuery', payload }),
    500,
  );

  return (
    <div className="layout-sidebar">
      <PageHeader
        className="area-page-header"
        title="Selecteer een klant"
        backLink={{
          to: '/dashboard',
        }}
      />
      <div className="area-page-content content-grid">
        <div className="col-span-full">
          <div className="flex items-center gap-x-16 mb-24">
            <Input
              icon={<IconSearch />}
              placeholder="Zoeken"
              className="w-full max-w-[550px] mr-auto"
              onChange={(event) => debouncedQuery(event.target.value)}
              defaultValue={state.search}
            />
            <ToggleButtonGroup
              defaultSelectedKey={
                state.filter?.find(([key]) => key === 'status')?.[1]
              }
              items={[
                { label: 'Alle', value: '' },
                { label: 'Actief', value: 'active' },
                { label: 'Prospect', value: 'prospect' },
                { label: 'Inactief', value: 'inactive' },
              ]}
              onChange={(status) =>
                setSearch({ type: 'setFilter', payload: ['status', status] })
              }
            />
          </div>
          <RadioGroup onChange={setActiveGuid}>
            <ul className="flex flex-col gap-y-24">
              {customers.map((customer) => (
                <li key={customer.guid}>
                  <UserRadioCard
                    value={customer.guid}
                    reference={customer.reference}
                    name={customer.consumer.fullName}
                    address={formatAddress(customer.address)}
                    tag={{
                      label: customer.status,
                      status: customer.status,
                    }}
                  />
                </li>
              ))}
            </ul>
          </RadioGroup>
          <Pagination
            {...pagination}
            variant="borderless"
            onLimitChange={(limit) =>
              setSearch({ type: 'setLimit', payload: limit })
            }
            onPageChange={(page) =>
              setSearch({ type: 'paginate', payload: page })
            }
          />
        </div>
      </div>
      <div className="flex flex-col area-page-sidebar">
        {activeCustomer && <CustomerInformation customer={activeCustomer} />}
        <ButtonLink
          intent="primary"
          iconRight={IconAngleRight}
          to="/add-insurance/$customer"
          params={{ customer: activeCustomer?.guid ?? '' }}
          disabled={!activeCustomer}
          className="mt-auto w-full justify-center"
        >
          Volgende
        </ButtonLink>
      </div>
    </div>
  );
}
